import { BaseComponent } from '../../components/base-component'
import {
  RentalPeriod,
  CalendarConfiguration,
  RentalIntentResponse,
  ShopifyVariant
} from '../../types/rental'

export class MultiVariantStep extends BaseComponent {
  private selectedOptions: Record<string, string | null> = {}
  private nextButton: HTMLButtonElement | null = null
  private variants: ShopifyVariant[] = []
  private currentVariant: ShopifyVariant | null = null
  private calendarConfig: CalendarConfiguration | null = null
  private rentalIntentToken: string | null = null
  private hasCreatedRentalIntent: boolean = false

  connectedCallback() {
    super.connectedCallback()
    this.initializeElements()
    this.initializeVariants()
    this.initializeOptions()
    this.initializeListeners()
    this.initializeCalendarConfig()

    // Ensure first option is enabled on initial load
    const firstSelect = this.querySelector('.product-configurator__input-block:first-of-type .product-configurator__select')
    if (firstSelect) {
      firstSelect.classList.remove('product-configurator__select--disabled')
      const trigger = firstSelect.querySelector('.product-configurator__select-trigger') as HTMLButtonElement
      if (trigger) {
        trigger.removeAttribute('disabled')
      }
    }

    this.updateValidity()
  }

  private initializeElements() {
    // Check for required data attributes
    if (!this.dataset.optionNames) {
      console.error('MultiVariantStep: Missing required data-option-names attribute')
      return
    }

    // Initialize next button if it exists
    this.nextButton = this.querySelector('.product-configurator__step-footer-button')
    if (!this.nextButton) {
      console.warn('MultiVariantStep: Next button not found, step validation will be disabled')
    }

    // Initialize option selects
    const optionNames = this.dataset.optionNames?.split(',') || []

    optionNames.forEach((_, index) => {
      const select = index === 0
        ? this.querySelector('.product-configurator__input-block:first-of-type .product-configurator__select')
        : this.querySelector(`.product-configurator__input-block:nth-of-type(${index + 1}) .product-configurator__select`)

      if (select) {
        if (index === 0) {
          select.classList.remove('product-configurator__select--disabled')
          const trigger = select.querySelector('.product-configurator__select-trigger') as HTMLButtonElement
          if (trigger) {
            trigger.removeAttribute('disabled')
          }
        } else {
          select.classList.add('product-configurator__select--disabled')
          const trigger = select.querySelector('.product-configurator__select-trigger') as HTMLButtonElement
          if (trigger) {
            trigger.setAttribute('disabled', '')
          }
        }
      }
    })
  }

  private initializeVariants() {
    const variantsScript = this.querySelector('.product-configurator__variants-json')
    if (!variantsScript) {
      console.error('MultiVariantStep: Missing variants JSON script')
      return
    }

    try {
      this.variants = JSON.parse(variantsScript.textContent || '[]')
    } catch (error) {
      console.error('MultiVariantStep: Failed to parse variants JSON:', error)
    }
  }

  private initializeOptions() {
    const optionNames = this.dataset.optionNames?.split(',') || []
    if (optionNames.length === 0) {
      console.error('MultiVariantStep: No option names provided')
      return
    }

    optionNames.forEach(name => {
      this.selectedOptions[name.trim()] = null
    })
  }

  private initializeListeners() {
    // Select triggers
    const selectTriggers = this.querySelectorAll('.product-configurator__select-trigger')
    selectTriggers.forEach(trigger => {
      this.addListener(trigger, 'click', this.handleSelectTriggerClick.bind(this))
    })

    // Select list items
    const selectListItems = this.querySelectorAll('.product-configurator__select-list-item')
    selectListItems.forEach(item => {
      this.addListener(item, 'click', this.handleSelectListItemClick.bind(this))
    })

    // Document click for closing selects
    this.addListener(document, 'click', this.handleDocumentClick.bind(this))
  }

  private handleSelectTriggerClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()

    const trigger = (event.currentTarget as HTMLElement)
    if (trigger.hasAttribute('disabled')) return

    const select = trigger.closest('.product-configurator__select')
    if (!select) return

    // Close any other open selects first
    const activeSelects = this.querySelectorAll('.product-configurator__select--active')
    activeSelects.forEach(activeSelect => {
      if (activeSelect !== select) {
        activeSelect.classList.remove('product-configurator__select--active')
      }
    })

    select.classList.toggle('product-configurator__select--active')
  }

  private handleSelectListItemClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()

    let item = event.target as HTMLButtonElement
    if(item.classList.contains('product-configurator__select-list-item-text')) {
      item = item.closest('.product-configurator__select-list-item') as HTMLButtonElement
    }
    if (item.disabled) return

    const select = item.closest('.product-configurator__select')
    const trigger = select?.querySelector('.product-configurator__select-trigger-text')
    const optionName = select?.closest('.product-configurator__input-block')?.querySelector('.product-configurator__input-label')?.textContent?.trim().toLowerCase()

    if (!select || !trigger || !optionName) return

    // Update trigger text and selected option
    trigger.textContent = item.textContent
    this.selectedOptions[optionName] = item.dataset.value || null

    // Close dropdown
    select.classList.remove('product-configurator__select--active')

    // Update available options and find current variant
    this.updateAvailableOptions()
    this.updateCurrentVariant()
    this.updateValidity()
  }

  private updateAvailableOptions() {
    const optionNames = this.dataset.optionNames?.split(',') || []

    // First, handle the first option (Chest) - it should ALWAYS be enabled with ALL options available
    const firstSelect = this.querySelector('.product-configurator__input-block:first-of-type .product-configurator__select')
    if (firstSelect) {
      firstSelect.classList.remove('product-configurator__select--disabled')
      const trigger = firstSelect.querySelector('.product-configurator__select-trigger') as HTMLButtonElement
      if (trigger) {
        trigger.removeAttribute('disabled')
      }

      // Enable ALL options in the first select
      const firstOptionButtons = firstSelect.querySelectorAll('.product-configurator__select-list-item')
      firstOptionButtons.forEach(button => {
        button.classList.remove('product-configurator__select-list-item--disabled')
        ;(button as HTMLButtonElement).disabled = false
        button.querySelector('.product-configurator__select-list-item-availability')?.remove()
        button.querySelector('.product-configurator__select-list-item-text')?.classList.remove('product-configurator__select-list-item-text--disabled')
      })
    }

    // Now handle subsequent options (Length)
    optionNames.forEach((optionName, index) => {
      if (index === 0) return // Skip first option as we've already handled it

      const select = this.querySelector(`.product-configurator__input-block:nth-of-type(${index + 1}) .product-configurator__select`)
      if (!select) return

      // Get the first option's selected value
      const firstOptionValue = this.selectedOptions[optionNames[0].trim()]

      if (!firstOptionValue) {
        // If no first option is selected, disable this option completely
        select.classList.add('product-configurator__select--disabled')
        const trigger = select.querySelector('.product-configurator__select-trigger') as HTMLButtonElement
        if (trigger) {
          trigger.setAttribute('disabled', '')
        }
        return
      }

      // If first option is selected, enable this option
      select.classList.remove('product-configurator__select--disabled')
      const trigger = select.querySelector('.product-configurator__select-trigger') as HTMLButtonElement
      if (trigger) {
        trigger.removeAttribute('disabled')
      }

      // Filter variants based on the selected first option
      const availableVariants = this.variants.filter(variant => {
        const matchesFirstOption = variant[`option1`] === firstOptionValue
        const isAvailableForRental = this.dataset.availableVariantIds.includes(variant.id.toString())
        return matchesFirstOption && isAvailableForRental
      })

      // Get available values for this option
      const availableValues = new Set(
        availableVariants.map(variant => variant[`option${index + 1}`])
      )

      // Update option buttons
      const optionButtons = select.querySelectorAll('.product-configurator__select-list-item')
      optionButtons.forEach(button => {
        const value = (button as HTMLElement).dataset.value
        const isAvailable = value ? availableValues.has(value) : false

        button.classList.toggle('product-configurator__select-list-item--disabled', !isAvailable)
        ;(button as HTMLButtonElement).disabled = !isAvailable

        // Handle availability text
        let availabilitySpan = button.querySelector('.product-configurator__select-list-item-availability')
        let textSpan = button.querySelector('.product-configurator__select-list-item-text')

        if (!isAvailable) {
          if (!availabilitySpan) {
            availabilitySpan = document.createElement('span')
            availabilitySpan.className = 'product-configurator__select-list-item-availability'
            availabilitySpan.textContent = 'Not available with selected chest size'
            button.appendChild(availabilitySpan)
          }
          textSpan?.classList.add('product-configurator__select-list-item-text--disabled')
        } else {
          availabilitySpan?.remove()
          textSpan?.classList.remove('product-configurator__select-list-item-text--disabled')
        }
      })
    })
  }

  private updateCurrentVariant() {
    const optionNames = this.dataset.optionNames?.split(',') || []

    this.currentVariant = this.variants.find(variant =>
      optionNames.every((name, index) =>
        variant[`option${index + 1}`] === this.selectedOptions[name.trim()]
      )
    ) || null

    // Store variant ID in dataset for later use
    if (this.currentVariant) {
      this.dataset.variantId = this.currentVariant.id.toString()
    } else {
      delete this.dataset.variantId
    }
  }

  isValid(): boolean {
    // If no variants are available at all, allow proceeding
    if (this.dataset.availableVariantIds === '') {
      return true
    }

    // Otherwise use existing validation logic
    return Object.values(this.selectedOptions).every(value => value !== null)
      && this.currentVariant !== null
      && this.currentVariant.available
  }

  private updateValidity() {
    if (!this.nextButton) return

    if (this.isValid()) {
      this.nextButton.classList.add('product-configurator__step-footer-button--active')
      this.nextButton.disabled = false
    } else {
      this.nextButton.classList.remove('product-configurator__step-footer-button--active')
      this.nextButton.disabled = true
    }
  }

  private handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (!target.closest('.product-configurator__select')) {
      const activeSelects = this.querySelectorAll('.product-configurator__select--active')
      activeSelects.forEach(select => {
        select.classList.remove('product-configurator__select--active')
      })
    }
  }

  activate() {
    this.classList.add('product-configurator__step--active')
  }

  deactivate() {
    this.classList.remove('product-configurator__step--active')
  }

  complete() {
    this.classList.add('product-configurator__step--completed')
  }

  getIndex(): number {
    const index = this.dataset.index
    if (!index) {
      console.error('MultiVariantStep: Missing data-index attribute')
      return -1
    }
    return parseInt(index)
  }

  updateAvailableVariants(availableVariantIds: number[]) {
    // Store available variant IDs in data attribute
    this.dataset.availableVariantIds = availableVariantIds.join(',')

    // Update options based on both Shopify and rental availability
    this.updateAvailableOptions()

    // Update validity
    this.updateValidity()
  }

  getCurrentVariantId(): number | null {
    return this.currentVariant?.id ?? null
  }

  private initializeCalendarConfig() {
    const calendarJson = this.querySelector('.product-configurator__calendar-json')?.textContent
    if (calendarJson) {
      this.calendarConfig = JSON.parse(calendarJson)
    }
  }

  getRentalPeriodByDuration(duration: number): RentalPeriod | null {
    return this.calendarConfig?.rental_periods.find(period => period.duration_count === duration) || null
  }

  hasRentalIntent(): boolean {
    return this.hasCreatedRentalIntent
  }

  setRentalIntentToken(response: RentalIntentResponse) {
    this.rentalIntentToken = JSON.stringify(response)
    this.hasCreatedRentalIntent = true
  }

  isAvailable(): boolean {
    return this.dataset.availableVariantIds !== ''
  }

  getRentalIntentToken(): string {
    return this.rentalIntentToken || '';
  }

  getProductDetails() {
    return {
      productId: this.dataset.productId || '',
      title: this.dataset.productTitle || '',
      options: this.selectedOptions
    }
  }

  reset() {
    // Reset selected options
    this.selectedOptions = {}
    this.currentVariant = null
    this.hasCreatedRentalIntent = false
    this.rentalIntentToken = null
    this.classList.remove('product-configurator__step--completed')

    // Reset all select triggers to default text
    this.querySelectorAll('.product-configurator__select-trigger-text').forEach(trigger => {
      trigger.textContent = 'Please choose'
    })

    // Reset all dropdowns to inactive state
    this.querySelectorAll('.product-configurator__select').forEach(select => {
      select.classList.remove('product-configurator__select--active')
    })

    // Re-enable all option buttons - with proper type casting
    this.querySelectorAll('.product-configurator__select-list-item').forEach(button => {
      (button as HTMLButtonElement).disabled = false
    })

    // Disable next button
    const nextButton = this.querySelector('.product-configurator__step-footer-button') as HTMLButtonElement
    if (nextButton) nextButton.disabled = true
  }
}